// src/pages/provider/ProviderServicesPage.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Wrench,
  Calendar,
  CheckCircle,
  Edit,
  Save,
  X,
  ChevronDown,
  Globe,
  FileText,
  Clock
} from 'lucide-react';

import useTranslation from '../../hooks/useTranslation';
import colors from '../../constants/colors';

// Redux imports
import {
  getProviderById,
  updateProvider,
  selectCurrentProvider,
  selectProviderLoading,
  selectProviderUpdating,
  selectProviderUpdateSuccess,
  selectProviderUpdateError,
  selectProviderError,
  clearCurrentProvider,
  resetUpdateState,
  clearErrors
} from '../../store/slices/providerSlice';
import {
  getReviewStatistics,
  selectReviewStatistics,
  selectReviewsLoading
} from '../../store/slices/serviceProviderReviewSlice';
import { selectUser, selectIsProvider } from '../../store/slices/authSlice';
import { selectProfile } from '../../store/slices/profileSlice';

import styles from '../../styles/ProviderServicesPage.module.css';

const ProviderServicesPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Redux selectors
  const user = useSelector(selectUser);
  const isProvider = useSelector(selectIsProvider);
  const provider = useSelector(selectCurrentProvider);
  const profile = useSelector(selectProfile);
  const isLoading = useSelector(selectProviderLoading);
  const isUpdating = useSelector(selectProviderUpdating);
  const updateSuccess = useSelector(selectProviderUpdateSuccess);
  const updateError = useSelector(selectProviderUpdateError);
  const error = useSelector(selectProviderError);
  const reviewStats = useSelector(selectReviewStatistics);
  const reviewsLoading = useSelector(selectReviewsLoading);
   
  // ======================================================
  // TRANSLATIONS - ALL HOOKS AT TOP LEVEL
  // ======================================================
  const { translatedText: myServices } = useTranslation('My Services');
  const { translatedText: serviceType } = useTranslation('Service Type');
  const { translatedText: yearsExperience } = useTranslation('Years Experience');
  const { translatedText: description } = useTranslation('Description');
  const { translatedText: descriptionPlaceholder } = useTranslation('Describe your services, experience, and what makes you unique...');
  const { translatedText: languages } = useTranslation('Languages');
  const { translatedText: workingHours } = useTranslation('Working Hours');
  const { translatedText: total_Reviews } = useTranslation('Total Reviews');
  const { translatedText: rating } = useTranslation('Rating');
  const { translatedText: selectServiceType } = useTranslation('Select Service Type');
  const { translatedText: saveChanges } = useTranslation('Save Changes');
  const { translatedText: cancel } = useTranslation('Cancel');
  const { translatedText: save } = useTranslation('Save');
  const { translatedText: success } = useTranslation('Success');
  const { translatedText: servicesUpdated } = useTranslation('Services updated successfully!');
  const { translatedText: errorTitle } = useTranslation('Error');
  const { translatedText: tryAgain } = useTranslation('Please try again');
  const { translatedText: loadingServices } = useTranslation('Loading services...');
  
  // Tips Translations
  const { translatedText: tipsTitle } = useTranslation('Tips for Success');
  const { translatedText: tip1 } = useTranslation('Keep your availability updated to get more bookings');
  const { translatedText: tip2 } = useTranslation('Add photos of your work to attract more clients');
  const { translatedText: tip3 } = useTranslation('Respond to client inquiries quickly');
  const { translatedText: tip4 } = useTranslation('Maintain a high rating through quality service');

  // ======================================================
  // FOOTER TRANSLATIONS
  // ======================================================
  const { translatedText: homeTitle } = useTranslation('Haitian Services');
  const { translatedText: quickLinks } = useTranslation('Quick Links');
  const { translatedText: findProviders } = useTranslation('Find Providers');
  const { translatedText: supportUs } = useTranslation('Support Us');
  const { translatedText: helpAndSupport } = useTranslation('Help & Support');
  const { translatedText: aboutUs } = useTranslation('About Us');
  const { translatedText: forProviders } = useTranslation('For Providers');
  const { translatedText: becomeAProvider } = useTranslation('Become a Provider');
  const { translatedText: providerLogin } = useTranslation('Provider Login');
  const { translatedText: dashboardLabel } = useTranslation('Dashboard');
  const { translatedText: settings } = useTranslation('Settings');
  const { translatedText: legal } = useTranslation('Legal');
  const { translatedText: termsOfService } = useTranslation('Terms of Service');
  const { translatedText: privacyPolicy } = useTranslation('Privacy Policy');
  const { translatedText: cookiePolicy } = useTranslation('Cookie Policy');
  const { translatedText: connectingHaitians } = useTranslation('Connecting Haitians Worldwide, One Service at a Time');
  const { translatedText: madeWithLove } = useTranslation('Made with ❤️ in Haiti');

  // Day Names Translations
  const { translatedText: monday } = useTranslation('Monday');
  const { translatedText: tuesday } = useTranslation('Tuesday');
  const { translatedText: wednesday } = useTranslation('Wednesday');
  const { translatedText: thursday } = useTranslation('Thursday');
  const { translatedText: friday } = useTranslation('Friday');
  const { translatedText: saturday } = useTranslation('Saturday');
  const { translatedText: sunday } = useTranslation('Sunday');

  // Service Types with translations
  const electrician = useTranslation('Electrician').translatedText;
  const plumber = useTranslation('Plumber').translatedText;
  const mason = useTranslation('Mason').translatedText;
  const painter = useTranslation('Painter').translatedText;
  const carpenter = useTranslation('Carpenter').translatedText;
  const barber = useTranslation('Barber').translatedText;
  const hvac = useTranslation('HVAC Technician').translatedText;
  const interiorDesigner = useTranslation('Interior Designer').translatedText;
  const landscaper = useTranslation('Landscaper').translatedText;
  const cleaner = useTranslation('Cleaner').translatedText;
  const handyman = useTranslation('Handyman').translatedText;
  const tiler = useTranslation('Tiler').translatedText;
  const solarInstaller = useTranslation('Solar Installer').translatedText;
  const flooringSpecialist = useTranslation('Flooring Specialist').translatedText;
  const other = useTranslation('Other').translatedText;

  const serviceTypes = [
    electrician,
    plumber,
    mason,
    painter,
    carpenter,
    barber,
    hvac,
    interiorDesigner,
    landscaper,
    cleaner,
    handyman,
    tiler,
    solarInstaller,
    flooringSpecialist,
    other
  ];

  // Language options translations
  const creole = useTranslation('Creole').translatedText;
  const french = useTranslation('French').translatedText;
  const english = useTranslation('English').translatedText;
  const spanish = useTranslation('Spanish').translatedText;

  const languagesOptions = [creole, french, english, spanish];

  const dayNames = {
    monday: monday,
    tuesday: tuesday,
    wednesday: wednesday,
    thursday: thursday,
    friday: friday,
    saturday: saturday,
    sunday: sunday
  };

  // Local state
  const [isEditing, setIsEditing] = useState(false);
  const [showServiceTypeModal, setShowServiceTypeModal] = useState(false);
  
  const [services, setServices] = useState({
    serviceType: '',
    yearsOfExperience: '',
    description: '',
    availability: {},
    languages: []
  });

  // ======================================================
  // GET STATS FROM REDUX
  // ======================================================
  const getTotalReviews = useCallback(() => {
    return reviewStats?.totalReviews || provider?.totalReviews || 0;
  }, [reviewStats, provider]);

  const getRating = useCallback(() => {
    return provider?.rating || 0;
  }, [provider]);

  // ======================================================
  // LOAD PROVIDER DATA
  // ======================================================
  const loadProviderData = useCallback(async () => {
    const providerId = profile?.providerProfile?._id;
    if (!providerId) return;
    
    try {
      await dispatch(getProviderById({ id: providerId })).unwrap();
      await dispatch(getReviewStatistics(providerId)).unwrap();
    } catch (error) {
      console.error('Failed to load provider:', error);
    }
  }, [dispatch, profile]);

  // Initial load
  useEffect(() => {
    loadProviderData();
  }, [loadProviderData]);

  // Update local state when provider data loads
  useEffect(() => {
    if (provider) {
      setServices({
        serviceType: provider.serviceType || '',
        yearsOfExperience: provider.yearsOfExperience ? provider.yearsOfExperience.toString() : '',
        description: provider.description || '',
        availability: provider.availability ? JSON.parse(JSON.stringify(provider.availability)) : {},
        languages: provider.languages ? [...provider.languages] : []
      });
    }
  }, [provider]);

  // Handle update success
  useEffect(() => {
    if (updateSuccess) {
      alert(`${success}\n\n${servicesUpdated}`);
      dispatch(resetUpdateState());
      setIsEditing(false);
      loadProviderData();
    }
  }, [updateSuccess, dispatch, loadProviderData, servicesUpdated, success]);

  // Handle errors
  useEffect(() => {
    if (updateError || error) {
      alert(`${errorTitle}\n\n${updateError || error || tryAgain}`);
      dispatch(clearErrors());
    }
  }, [updateError, error, dispatch, errorTitle, tryAgain]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(clearCurrentProvider());
      dispatch(resetUpdateState());
      dispatch(clearErrors());
    };
  }, [dispatch]);

  const handleSave = () => {
    if (window.confirm(`${saveChanges}\n\nAre you sure you want to save these changes?`)) {
      const providerId = profile?.providerProfile?._id;
      if (!providerId) {
        alert(`${errorTitle}\n\nProvider ID not found`);
        return;
      }
      
      const updateData = {
        serviceType: services.serviceType,
        yearsOfExperience: parseInt(services.yearsOfExperience) || 0,
        description: services.description,
        availability: services.availability,
        languages: services.languages
      };
      
      dispatch(updateProvider({ id: providerId, data: updateData }));
    }
  };

  const toggleAvailability = (day) => {
    setServices({
      ...services,
      availability: {
        ...services.availability,
        [day]: {
          ...services.availability[day],
          available: !services.availability[day].available
        }
      }
    });
  };

  const updateAvailabilityTime = (day, field, value) => {
    setServices({
      ...services,
      availability: {
        ...services.availability,
        [day]: {
          ...services.availability[day],
          [field]: value
        }
      }
    });
  };

  const toggleLanguage = (lang) => {
    if (services.languages.includes(lang)) {
      setServices({
        ...services,
        languages: services.languages.filter(l => l !== lang)
      });
    } else {
      setServices({
        ...services,
        languages: [...services.languages, lang]
      });
    }
  };

  const renderAvailabilityRow = (day, schedule) => {
    const dayName = dayNames[day] || day;

    return (
      <div key={day} className={styles.availabilityRow}>
        <div className={styles.availabilityDayContainer}>
          <span className={styles.availabilityDayName}>{dayName}</span>
          <button
            className={`${styles.availabilitySwitch} ${schedule.available ? styles.active : ''}`}
            onClick={() => toggleAvailability(day)}
            disabled={!isEditing}
          />
        </div>
        {schedule.available && (
          <div className={styles.availabilityTimeContainer}>
            <input
              type="text"
              className={styles.timeInput}
              value={schedule.start}
              onChange={(e) => updateAvailabilityTime(day, 'start', e.target.value)}
              disabled={!isEditing}
            />
            <span className={styles.timeSeparator}>to</span>
            <input
              type="text"
              className={styles.timeInput}
              value={schedule.end}
              onChange={(e) => updateAvailabilityTime(day, 'end', e.target.value)}
              disabled={!isEditing}
            />
          </div>
        )}
      </div>
    );
  };

  // Loading state
  if (isLoading && !provider) {
    return (
      <div className={styles.providerServicesContainer}>
        <div className={styles.servicesHeader}>
          <h2>{myServices}</h2>
        </div>
        <div className={styles.loadingContainer}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className={styles.loadingText}>{loadingServices}</p>
        </div>
      </div>
    );
  }

  // ======================================================
  // GET STATS VALUES
  // ======================================================
  const totalReviews = getTotalReviews();
  const ratingValue = getRating();

  return (
    <div className={styles.providerServicesContainer}>
      {/* Header */}
      <div className={styles.servicesHeader}>
        <h2>{myServices}</h2>
        <button 
          className={`${styles.editServicesBtn} ${isEditing ? styles.success : styles.primary}`}
          onClick={() => isEditing ? handleSave() : setIsEditing(true)}
          disabled={isUpdating}
        >
          {isUpdating ? (
            <div className="spinner-border spinner-border-sm text-white" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          ) : (
            <>
              {isEditing ? <Save size={18} /> : <Edit size={18} />}
              {isEditing ? saveChanges : 'Edit'}
            </>
          )}
        </button>
      </div>

      <div className={styles.servicesContent}>
        {/* Left Column */}
        <div className={styles.leftColumn}>
          {/* Service Type */}
          <div className={styles.section}>
            <h6 className={styles.sectionTitle}>
              <Wrench size={20} color={colors.primary} />
              {serviceType}
            </h6>
            <button 
              className={styles.serviceTypeSelector}
              onClick={() => isEditing && setShowServiceTypeModal(true)}
              disabled={!isEditing}
            >
              <span className={styles.serviceTypeDisplayText}>{services.serviceType || 'Select a service'}</span>
              {isEditing && <ChevronDown size={20} color={colors.primary} />}
            </button>
          </div>

          {/* Years Experience */}
          <div className={styles.section}>
            <h6 className={styles.sectionTitle}>
              <Clock size={20} color={colors.primary} />
              {yearsExperience}
            </h6>
            {isEditing ? (
              <input
                type="number"
                className={styles.pricingInput}
                value={services.yearsOfExperience}
                onChange={(e) => setServices({ ...services, yearsOfExperience: e.target.value })}
                placeholder="Enter years of experience"
                min="0"
                max="99"
              />
            ) : (
              <div className={styles.pricingValue}>{services.yearsOfExperience || '0'} years</div>
            )}
          </div>

          {/* Description */}
          <div className={styles.section}>
            <h6 className={styles.sectionTitle}>
              <FileText size={20} color={colors.primary} />
              {description}
            </h6>
            {isEditing ? (
              <textarea
                className={styles.descriptionInput}
                value={services.description}
                onChange={(e) => setServices({ ...services, description: e.target.value })}
                rows={4}
                placeholder={descriptionPlaceholder}
              />
            ) : (
              <div className={styles.descriptionText}>
                {services.description || 'No description provided.'}
              </div>
            )}
          </div>
        </div>

        {/* Right Column */}
        <div className={styles.rightColumn}>
          {/* Languages */}
          <div className={styles.section}>
            <h6 className={styles.sectionTitle}>
              <Globe size={20} color={colors.primary} />
              {languages}
            </h6>
            <div className={styles.languagesContainer}>
              {languagesOptions.map((lang) => (
                <button
                  key={lang}
                  className={`${styles.languageTag} ${services.languages.includes(lang) ? styles.active : ''}`}
                  onClick={() => isEditing && toggleLanguage(lang)}
                  disabled={!isEditing}
                >
                  {lang}
                </button>
              ))}
            </div>
          </div>

          {/* Availability */}
          <div className={styles.section}>
            <h6 className={styles.sectionTitle}>
              <Calendar size={20} color={colors.primary} />
              {workingHours}
            </h6>
            <div className={styles.availabilityContainer}>
              {Object.entries(services.availability).map(([day, schedule]) => 
                renderAvailabilityRow(day, schedule)
              )}
            </div>
          </div>

          {/* Stats - Jobs Completed REMOVED */}
          <div className={styles.statsContainer}>
            <div className={styles.statItem}>
              <div className={styles.statValue}>{totalReviews}</div>
              <div className={styles.statLabel}>{total_Reviews}</div>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.statItem}>
              <div className={styles.statValue}>{ratingValue.toFixed(1)}</div>
              <div className={styles.statLabel}>{rating}</div>
            </div>
          </div>

          {/* Tips */}
          <div className={styles.tipsContainer}>
            <h6 className={styles.tipsTitle}>💡 {tipsTitle}</h6>
            {[tip1, tip2, tip3, tip4].map((tip, index) => (
              <div key={index} className={styles.tipItem}>
                <CheckCircle size={16} color={colors.success} />
                <span className={styles.tipText}>{tip}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Service Type Modal */}
      {showServiceTypeModal && (
        <div className={styles.modalOverlay} onClick={() => setShowServiceTypeModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{selectServiceType}</h2>
              <button className={styles.modalClose} onClick={() => setShowServiceTypeModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className={styles.modalBody}>
              {serviceTypes.map((item) => (
                <button
                  key={item}
                  className={styles.serviceTypeOption}
                  onClick={() => {
                    setServices({ ...services, serviceType: item });
                    setShowServiceTypeModal(false);
                  }}
                >
                  <span className={`${styles.serviceTypeOptionText} ${services.serviceType === item ? styles.active : ''}`}>
                    {item}
                  </span>
                  {services.serviceType === item && (
                    <CheckCircle size={20} color={colors.primary} />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Footer - FULLY TRANSLATED */}
      <footer className={styles.footerFull}>
        <div className={styles.footerContent}>
          <div className={styles.footerGridFull}>
            {/* Brand Column */}
            <div className={styles.footerBrandColumn}>
              <div className={styles.footerBrandContainer}>
                <div className={styles.footerLogoWrapper}>
                  <img 
                    src="/logo/logo_hsm.png" 
                    alt="Haitian Services"
                    className={styles.footerLogo}
                  />
                </div>
                <div>
                  <div className={styles.footerBrandName}>{homeTitle}</div>
                  <div className={styles.footerBrandDescription}>{connectingHaitians}</div>
                </div>
              </div>
              <div className={styles.footerSocialLinks}>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-facebook.svg" alt="Facebook" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-instagram.svg" alt="Instagram" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-twitter.svg" alt="Twitter" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-youtube.svg" alt="YouTube" width="20" height="20" />
                </a>
              </div>
            </div>

            {/* Quick Links - TRANSLATED */}
            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{quickLinks}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/providers">{findProviders}</a></li>
                <li><a href="/donation">{supportUs}</a></li>
                <li><a href="/help-support">{helpAndSupport}</a></li>
                <li><a href="/about">{aboutUs}</a></li>
              </ul>
            </div>

            {/* For Providers - TRANSLATED */}
            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{forProviders}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/register">{becomeAProvider}</a></li>
                <li><a href="/login">{providerLogin}</a></li>
                <li><a href="/provider-dashboard">{dashboardLabel}</a></li>
                <li><a href="/provider-settings">{settings}</a></li>
              </ul>
            </div>

            {/* Legal - TRANSLATED */}
            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{legal}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/terms">{termsOfService}</a></li>
                <li><a href="/privacy">{privacyPolicy}</a></li>
                <li><a href="/cookies">{cookiePolicy}</a></li>
              </ul>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className={styles.footerDividerFull}></div>
          <div className={styles.footerBottomFull}>
            <div className={styles.footerFlag}>
              <div className={styles.flagStripes}>
                <div className={`${styles.stripe} ${styles.stripeBlue}`}></div>
                <div className={`${styles.stripe} ${styles.stripeRed}`}></div>
              </div>
            </div>
            <div className={styles.footerBottomText}>
              © 2026 {homeTitle}. {madeWithLove}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProviderServicesPage;